import React from "react";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import Tooltip from "@directv-digital/fusion.molecules.tooltip";
import { CheckIcon } from "@directv-digital/fusion.atoms.icons";

// Helper to render tooltip content with mobile-friendly link support
const _TooltipContentWrapper = ({ raw }: { raw: any }) => {
  if (raw == null) {
    return null;
  }
  // If string may contain <a> tags (CMS provided). Use innerHTML.
  if (typeof raw === 'string') {
    return (
      <span

        dangerouslySetInnerHTML={{ __html: raw }}
        style={{ display: 'inline-block' }}
        onClick={e => {
          // Allow navigation but keep tooltip from closing before click registers
          e.stopPropagation();
        }}
        onTouchStart={e => {
          e.stopPropagation();
        }}
      />
    );
  }
  // ReactNode passed directly
  return (
    <span
      style={{ display: 'inline-block' }}
      onClick={e => e.stopPropagation()}
      onTouchStart={e => e.stopPropagation()}
    >
      {raw}
    </span>
  );
};

// Helper to normalize tooltip raw value into a string (library expects string for html-react-parser)
const normalizeTooltip = (raw: any): string => {
  if (raw == null) {
    return "";
  }
  if (typeof raw === "string") {
    return raw;
  }
  if (Array.isArray(raw)) {
    const first = raw[0];
    if (typeof first === 'string') {
      return first;
    }
    if (first && typeof first === 'object') {
      if (typeof first.content === 'string') {
        return first.content;
      }
    }
  }
  if (typeof raw === 'object') {
    if (typeof raw.content === 'string') {
      return raw.content;
    }
  }
  try {
    return String(raw);
  } catch {
    return "";
  }
};

export function DtvCompetitorComparisonTableBody(props: any) {
  const {
    features,
    featureColWidth,
    dtvColWidth,
    competitorColWidth,
    mainPackageDetails,
    competitorDetails,
    aria,
    isIcon,
    renderValue,
    formatAria,
    isGenrePack
  } = props || {};

  // Simple cell extractor
  // Returns: { iconValue: 'true'|'false'|'NA'|string|object, extraText?: string }
  const extractCell = (raw: any) => {
    try {
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        // Pass through structured monthlyCost objects from adapter
        if (raw?.strikeoutText || raw?.offerText) {
          return { iconValue: raw };
        }
        const keys = Object.keys(raw);
        if (keys.length === 0) {
          return { iconValue: 'NA' };
        }
        const available = (raw as any)?.available;
        const value = (raw as any)?.value ?? '';
        if (available === true || available === 'true') {
          return {
            iconValue: 'true',
            extraText: value
          };
        }
        if (available === false || available === 'false') {
          return {
            iconValue: 'false',
            extraText: value
          };
        }
        // If object but no available flag just stringify first meaningful value
        if (value) {
          return { iconValue: value };
        }
        return { iconValue: 'NA' };
      }
      if (raw === undefined || raw === null || String(raw).trim().toLowerCase() === 'null') {
        return { iconValue: 'NA' };
      }
      const str = String(raw).trim();
      if (str.toLowerCase() === 'na') {
        return { iconValue: 'NA' };
      }
      return { iconValue: str };
    } catch {
      return { iconValue: 'NA' };
    }
  };

  return (
    <TableBody>
      {features?.map(({ key, label, tooltip, logo }: any, idx: number) => {
        const isLast = idx === features?.length - 1;

        const dtvCell = extractCell(mainPackageDetails?.[key]);
        const compCell = extractCell(competitorDetails?.[key]);

        // Build readable aria values — handle structured monthlyCost objects
        const getAriaValue = (cell: any) => {
          if (cell.iconValue && typeof cell.iconValue === 'object' && (cell.iconValue.strikeoutText || cell.iconValue.offerText)) {
            return `${cell.iconValue.strikeoutText || ""} ${cell.iconValue.offerText || ""}`.trim();
          }
          if (cell.iconValue === 'true') {
 return aria?.yes;
}
          if (cell.iconValue === 'false') {
 return aria?.no;
}
          if (cell.iconValue === 'NA') {
 return aria?.notAvailable;
}
          return cell?.extraText || cell.iconValue;
        };
        const dtvAriaValue = getAriaValue(dtvCell);
        const compAriaValue = getAriaValue(compCell);

        return (
          <TableRow key={key} sx={{ background: "transparent" }}>
            {/* Feature column */}
            <TableCell
              sx={{
                width: featureColWidth,
                minWidth: featureColWidth,
                background: "transparent",
                padding: {
 xs: "12px 12px",
sm: "24px 16px",
md: "24px 16px"
},
                boxSizing: "border-box",
                verticalAlign: "top",
                fontWeight: 500,
                fontSize: 14,
                color: "#222",
                borderBottom: isLast && "none"
              }}
            >
              {logo ? (
                <DtvBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    minHeight: 24,
                    pr: "2px"
                  }}
                >
                  <DtvBox
                    alt={logo?.alt || label}
                    component="img"
                    height="80px"
                    src={logo?.src}
                    width="80px"
                    sx={{
                      display: "block",
objectFit: "contain",
                      my: "auto",
...(logo?.logoStyle || {})
                    }}
                  />
                </DtvBox>
              ) : (
                <DtvText
                  aria-label={label}
                  content={label}
                  variant={isGenrePack ? "body3medium" : "body3"}
                  sx={{
 wordBreak: "normal",
paddingRight: "2px"
}}
                />
              )}
              {tooltip && (
                (() => {
 const tooltipContent = normalizeTooltip(tooltip); return tooltipContent ? (
                <Tooltip
                  aria-label={tooltipContent.replace(/<[^>]*>/g, '')}
                  closeOnScroll={true}
                  content={tooltipContent}
                  enterTouchDelay={0}
                  leaveTouchDelay={4000}
                  placement="top"
                  PopperProps={{
                    sx: {
                      '& .MuiTooltip-tooltip, & .MuiPaper-root': {
                        pointerEvents: 'auto'
                      },
                      '& a': {
                        color: '#006DFF',
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      },
                      '& .MuiTypography-root': {
                        fontFamily: 'roboto-regular',
                        fontSize: '14px !important'
                      }
                    }
                  }}
                />) : null;
})()
              )}
            </TableCell>
            {/* DIRECTV package column */}
            <TableCell
              aria-label={formatAria?.(aria?.featureLabel, {
                col: aria?.dtvColLabel,
                feature: label,
                value: dtvAriaValue
              })}
              sx={{
                width: dtvColWidth,
                minWidth: dtvColWidth,
                maxWidth: dtvColWidth,
                borderLeft: "5px solid #006DFF",
                borderRight: "5px solid #09F0FF",
                borderBottomColor: "#D1D1D1",
                borderBottom: isLast ? "5px solid #09F0FF" : "none",
                borderImage: "linear-gradient(to right, #006DFF, #00F0FF) 1",
                borderRightWidth: "5px",
                borderRightStyle: "solid",
                borderBottomLeftRadius: isLast ? 12 : 0,
                borderBottomRightRadius: isLast ? 12 : 0,
                backgroundColor: "#FBFBFB !important",
                position: "relative",
                boxSizing: "border-box",
                padding: {
 xs: "12px 12px",
sm: "24px 24px",
md: "24px 24px"
},
                verticalAlign: "top",
                textAlign: "left",
                "&:not(:first-of-type)::before": {
                  content: '""',
                  position: "absolute",
                  top: "-1px",
                  left: 0,
                  right: 0,
                  height: "0.7px",
                  background: "#D1D1D1",
                  opacity: 0.6,
                  pointerEvents: "none"
                }
              }}
            >
              {isIcon(dtvCell.iconValue) ? (
                dtvCell?.extraText ? (
                  <DtvBox sx={{
 width: "100%",
display: "flex",
flexDirection: "column",
alignItems: "center",
justifyContent: "center",
padding: {
 xs: "12px",
sm: "unset"
}
}}>
                    {renderValue(dtvCell.iconValue, label, aria?.dtvColLabel, aria, isGenrePack)}
                    <DtvText content={dtvCell?.extraText} variant="body3" sx={{
 fontSize: "12px !important",
wordBreak: "normal",
textAlign: "center",
mt: "4px"
}} />
                  </DtvBox>
                ) : (
                  <DtvBox sx={{
 width: "100%",
display: "flex",
alignItems: "center",
justifyContent: "center",
padding: {
 xs: "12px",
sm: "unset"
}
}}>
                    {renderValue(dtvCell.iconValue, label, aria?.dtvColLabel, aria, isGenrePack)}
                  </DtvBox>
                )
              ) : (
                renderValue(dtvCell.iconValue, label, aria?.dtvColLabel, aria, isGenrePack)
              )}
            </TableCell>
            {/* Competitor package column */}
            <TableCell
              aria-label={formatAria(aria?.featureLabel, {
                col: aria?.competitorColLabel,
                feature: label,
                value: compAriaValue
              })}
              sx={{
                width: competitorColWidth,
                minWidth: competitorColWidth,
                maxWidth: competitorColWidth,
                background: "transparent",
                padding: {
 xs: "12px 12px",
sm: "24px 24px",
md: "24px 56px"
},
                boxSizing: "border-box",
                verticalAlign: "top",
                borderBottom: isLast && "none",
                textAlign: isGenrePack ? "left" : "center"
              }}
            >
              {isIcon(compCell.iconValue) ? (
                compCell?.extraText ? (
                  <DtvBox sx={{
 width: "100%",
display: "flex",
flexDirection: "column",
alignItems: "center",
justifyContent: "center",
padding: {
 xs: "12px",
sm: "unset"
}
}}>
                    <CheckIcon desc={compCell?.extraText} fill="#8D8D8D" title={compCell?.iconValue} sx={{
 fontSize: 22,
marginBottom: "2px"
}} />
                    <DtvText content={compCell?.extraText} variant="body3" sx={{
 fontSize: "12px !important",
wordBreak: "normal",
textAlign: {
 xs: "left",
sm: "center"
},
mt: "4px"
}} />
                  </DtvBox>
                ) : (
                  <DtvBox sx={{
 width: "100%",
display: "flex",
alignItems: "center",
justifyContent: "center",
padding: {
 xs: "12px",
sm: "unset"
}
}}>
                    {renderValue(compCell.iconValue, label, aria?.competitorColLabel, aria, isGenrePack)}
                  </DtvBox>
                )
              ) : (
                renderValue(compCell.iconValue, label, aria?.competitorColLabel, aria, isGenrePack)
              )}
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  );
}
